import { get, isEmpty, isEqual } from 'lodash-es'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TRACKING_EVENT_NAMES, fireBookingPageShared } from 'hooks/analytics_events'

import { ANALYTICS_ACTION_TYPE } from './constants'
import { sulAnalyticsFormInitialValuesSelector } from './selectors'
import { setSULAnalyticsFormInitialValues } from './slice'

export const trackContactUpdatedEvent = (track, data) => {
  track({
    name: TRACKING_EVENT_NAMES.CONTACT_UPDATED,
    properties: {
      update_type: 'create',
      contact_source: 'sul',
      contact_uuid: data.contact_uuid
    }
  })
}

export const trackFlowStarted = (track) => track({
  name: TRACKING_EVENT_NAMES.FLOW_STARTED,
  properties: {
    flow_location: 'et_card',
    flow_name: 'sul',
    flow_version: 'v2'
  }
})

export const trackBookingPageShared = (track, data) => {
  fireBookingPageShared(track, {
    share_location: 'sul',
    share_method: data.shareMethod,
    customized_details: data.customizedDetails || [],
    share_object: 'event_type',
    is_single_use: true,
    is_customized_at_share: data.isCustomizedAtShare,
    is_reschedule_link: false,
    event_type_uuid: data.eventTypeUuid
  }, data.isAiNotetakerEnabled)
}

const isValueChangedForFields = (values, initState) => (...fields) => (
  fields.some((field) => {
    // Check equality for array fields
    // e.g. memberships[].someField or locationConfigurations[].someField
    // IMPORTANT! Works only for one level deep array fields
    if (/\[\]/.test(field)) {
      const arrayPath = field.split('[]')[0]
      const maxLength = Math.max(
        get(values, `${arrayPath}.length`, 0),
        get(initState, `${arrayPath}.length`, 0)
      )

      /// Somewhere here is a bug with returning false!!!!!
      for (let i = 0; i < maxLength; i += 1) {
        const getQuery = field.replace(/\[\]/, `[${i}]`)
        if (!isEqual(get(values, getQuery), get(initState, getQuery))) return true
      }
    }

    return !isEqual(get(values, field), get(initState, field))
  })
)

const AnalyticsContext = createContext({})
export const useSingleUseLinkAnalyticsContext = () => useContext(AnalyticsContext)

export const AnalyticsContextProvider = ({ children }) => {
  const dispatch = useDispatch()
  const initialValuesStore = useSelector(sulAnalyticsFormInitialValuesSelector)


  const resetFormInitialValuesFor = useCallback((action) => (formNewValues) => {
    dispatch(setSULAnalyticsFormInitialValues({ [action]: formNewValues }))
  }, [dispatch])

  // Track Event "customized_details" field: [duration, location, availability, hosts, event_name, contact_added]
  const getCustomizedDetails = useCallback((action) => (values = {}) => {
    const customizedDetails = []
    const initStateByAction = initialValuesStore[action]

    const isChangedFor = isValueChangedForFields(values, initStateByAction)
    if (isChangedFor('eventDuration', 'customEventDuration', 'customEventDurationType', 'durationOptions')) {
      customizedDetails.push('duration')
    }
    if (isChangedFor(
      'locationConfigurations.length',
      'locationConfigurations[].additionalInfo',
      'locationConfigurations[].hideLocation',
      'locationConfigurations[].kind',
      'locationConfigurations[].location',
      'locationConfigurations[].phoneNumber',
    )) {
      customizedDetails.push('location')
    }
    if (isChangedFor(
      'shareOverrideAvailability.availableHours',
      'shareOverrideAvailability.dateOverrides',
      'shareOverrideAvailability.timezone',
      'shareOverrideAvailability.usingCustomHours',
      'shareOverrideAvailability.usingSecondarySchedule',
    )) {
      customizedDetails.push('availability')
    }
    if (isChangedFor('memberships')) {
      customizedDetails.push('hosts')
    }
    if (isChangedFor('name')) {
      customizedDetails.push('event_name')
    }
    if (isChangedFor('contact')) {
      customizedDetails.push('contact_added')
    }

    return customizedDetails
  }, [initialValuesStore])

  const values = useMemo(() => ({
    getCopyLinkCustomizedDetails: getCustomizedDetails(ANALYTICS_ACTION_TYPE.COPY_LINK),
    getShareLinkCustomizedDetails: getCustomizedDetails(ANALYTICS_ACTION_TYPE.SHARE),
    resetCopyLinkCustomizedDetails: resetFormInitialValuesFor(ANALYTICS_ACTION_TYPE.COPY_LINK),
    resetShareLinkCustomizedDetails: resetFormInitialValuesFor(ANALYTICS_ACTION_TYPE.SHARE)
  }), [resetFormInitialValuesFor, getCustomizedDetails])

  return (
    <AnalyticsContext.Provider value={values}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const InitializeAnalytics = ({ initialValues }) => {
  const dispatch = useDispatch()
  const initialValuesStore = useSelector(sulAnalyticsFormInitialValuesSelector)

  const initializeAnalytics = useCallback((formInitialValues) => {
    if (!isEmpty(initialValuesStore)) return

    dispatch(setSULAnalyticsFormInitialValues({
      [ANALYTICS_ACTION_TYPE.COPY_LINK]: formInitialValues, // Copy Link initial values form state
      [ANALYTICS_ACTION_TYPE.SHARE]: formInitialValues // Share Link initial values form state
    }))
  }, [initialValuesStore, dispatch])

  useEffect(() => {
    if (!initialValues) return

    initializeAnalytics(initialValues)
  }, [initialValues, initializeAnalytics])

  return null
}
